import {
    all, put, SelectEffect, takeLatest,
} from "@redux-saga/core/effects";
import {Nullable, safeSaga, transformRequestOptions} from "@data-force/app";
import axios, {AxiosResponse} from "axios";
import {CallEffect, PutEffect, select} from "redux-saga/effects";
import XLSX, {WorkBook} from "xlsx";
import {GetJobListPhaseStatsResponse, GetJobPhaseListResponse} from "../../interfaces/store/sagas/job";
import {SetAnnouncementsAction, SetHomeJobsAction} from "../../interfaces/store/actions/home";
import {getBaseUrl} from "./main";
import {GET_HOME_DATA} from "../actions/availableActions";
import {setAnnouncements, setHomeJobs} from "../actions/home";
import {JobFilterType, JobTag} from "../../enums/enums";
import {
    AnnouncementData, CommonJobParams, JobsHomeData,
} from "../../interfaces/store/actions/payload/home";
import {setLoading} from "../actions/mask";
import {SetLoadingAction} from "../../interfaces/store/actions/mask";
import StoreState from "../../interfaces/store/storeState";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {TaskPhaseStats} from "../../interfaces/store/actions/payload/task";

type GetHomeDataHandlerIteratorType =
    SelectEffect
    | Promise<void | Response | AxiosResponse<GetJobListPhaseStatsResponse
    | GetJobPhaseListResponse>>
    | PutEffect<SetLoadingAction | SetHomeJobsAction | SetAnnouncementsAction>
    | CallEffect;
function* getHomeDataHandler(): IterableIterator<GetHomeDataHandlerIteratorType> {
    yield put(setLoading(true));
    const baseUrl: Nullable<string> = yield getBaseUrl();
    const jobData: JobsHomeData = {
        availableJobs: [],
        featuredJobs: [],
        inProgressJobs: [],
    };

    const commonParams: CommonJobParams = {
        limit: "10",
        sortDirection: "DESC",
    };

    const featuredResponse: Nullable<AxiosResponse<GetJobPhaseListResponse>> = yield axios.get(`${baseUrl}/job/phase`, {
        params: {
            ...commonParams,
            filter: JobFilterType.CLAIMABLE,
            tags: [JobTag.FEATURED],
        },
        paramsSerializer: (params) => transformRequestOptions(params),
    });

    if (featuredResponse) {
        const {data}: AxiosResponse<GetJobPhaseListResponse> = featuredResponse;
        if (data?.items) {
            jobData.featuredJobs = data.items;
        }
    }

    const availableResponse: Nullable<AxiosResponse<GetJobPhaseListResponse>> = yield axios.get(`${baseUrl}/job/phase`, {
        params: {
            ...commonParams,
            filter: JobFilterType.CLAIMABLE,
        },
        paramsSerializer: (params) => transformRequestOptions(params),
    });

    if (availableResponse) {
        const {data}: AxiosResponse<GetJobPhaseListResponse> = availableResponse;
        if (data?.items) {
            jobData.availableJobs = data.items;
        }
    }

    const inProgressResponse: Nullable<AxiosResponse<GetJobPhaseListResponse>> = yield axios.get(`${baseUrl}/job/phase`, {
        params: {
            ...commonParams,
            filter: JobFilterType.ONGOING,
        },
        paramsSerializer: (params) => transformRequestOptions(params),
    });

    if (inProgressResponse) {
        const {data}: AxiosResponse<GetJobPhaseListResponse> = inProgressResponse;
        if (data?.items) {
            jobData.inProgressJobs = data.items.map((job: JobEntity): JobEntity => {
                const {phaseStats}: JobEntity = job;
                let completedTaskCount: number = 0;

                if (phaseStats) {
                    const {
                        COMPLETED = 0,
                        REJECTED = 0,
                    }: TaskPhaseStats = phaseStats;
                    completedTaskCount = COMPLETED + REJECTED;
                }

                return {
                    ...job,
                    completedTaskCount,
                };
            });
        }
    }

    const url: Nullable<string> = yield select(
        (state: StoreState): Nullable<string> => state.serviceProperties.announcementsFileUrl,
    );
    let announcements: AnnouncementData[] = [];

    if (url) {
        yield fetch(url).then((res: Response) => res.arrayBuffer()).then((arrayBuffer:ArrayBuffer) => {
            /* parse the data when it is received */
            const data: Uint8Array = new Uint8Array(arrayBuffer);
            const workBook: WorkBook = XLSX.read(data, {type: "array"});
            const firstSheetName: string = workBook.SheetNames[0];
            announcements = XLSX.utils.sheet_to_json(
                workBook.Sheets[firstSheetName], {header: 0, raw: false},
            );
        });
    }

    yield put(setAnnouncements(announcements));
    yield put(setHomeJobs(jobData));
    yield put(setLoading(false));
}

export function* homeSagaActionWatcher() {
    yield all([
        takeLatest(GET_HOME_DATA, safeSaga(getHomeDataHandler)),
    ]);
}
